import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { SearchableSelectWithAction } from '../../components/ui/searchable-select-with-action';
import { Plus, Trash2, Package, Link2, ArrowRightLeft, Pencil, Lightbulb, BookOpen } from 'lucide-react';
import { Checkbox } from '../../components/ui/checkbox';

const LBS_TO_KG = 0.45359237;

export function OrderLineItemsTable({
  lines, products, cobrandings,
  supplierCodeOptions, customerId,
  isReadOnly, totals,
  weightInLbs, onToggleWeightUnit,
  onAddLine, onRemoveLine, onUpdateLine,
  onSupplierCodeSelect, onCreateNewSupplierCode,
  onEditSupplierCode,
  onProductSelect, onPackagingChange,
  onFormulaOverrideToggle,
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between py-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Package className="w-4 h-4" />
          Order Lines
        </CardTitle>
        {!isReadOnly && (
          <Button variant="outline" size="sm" onClick={onAddLine} data-testid="btn-add-line" className="h-8">
            <Plus className="w-4 h-4 mr-1" />
            Add Line
          </Button>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="text-left p-2 font-medium text-xs w-[130px] max-w-[130px]">Customer Code</th>
                <th className="text-left p-2 font-medium text-xs min-w-[140px]">Product *</th>
                <th className="text-left p-2 font-medium text-xs w-[110px] max-w-[110px]">Packaging</th>
                <th className="text-right p-2 font-medium text-xs w-[80px]">No. Units *</th>
                <th className="text-right p-2 font-medium text-xs w-[70px]">Cases</th>
                <th className="text-right p-2 font-medium text-xs w-[85px]">
                  <button
                    type="button"
                    onClick={onToggleWeightUnit}
                    className="inline-flex items-center gap-1 hover:text-primary transition-colors"
                    title={weightInLbs ? 'Switch to Kilograms' : 'Switch to Pounds'}
                    data-testid="btn-toggle-weight-unit"
                  >
                    Wt ({weightInLbs ? 'lbs' : 'kg'})
                    <ArrowRightLeft className="w-3 h-3" />
                  </button>
                </th>
                <th className="text-left p-2 font-medium text-xs w-[90px]">Notes</th>
                {!isReadOnly && <th className="w-[36px]"></th>}
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <React.Fragment key={line.id}>
                <tr className="border-b hover:bg-muted/30">
                  <td className="p-1 max-w-[130px]">
                    <div className="flex items-center gap-0.5">
                      <SearchableSelectWithAction
                        options={supplierCodeOptions}
                        value={line.supplier_product_code || ''}
                        onValueChange={(value) => onSupplierCodeSelect(index, value)}
                        placeholder={customerId ? "Code..." : "Select cust."}
                        searchPlaceholder="Type to search..."
                        emptyMessage="No codes found"
                        disabled={isReadOnly || !customerId}
                        onCreateNew={(searchText) => onCreateNewSupplierCode(index, searchText)}
                        createNewLabel="+ Create New Code"
                        triggerClassName="max-w-[110px]"
                        data-testid={`select-supplier-code-${index}`}
                      />
                      {line.supplier_product_code && !isReadOnly && (
                        <button
                          type="button"
                          onClick={() => onEditSupplierCode(index)}
                          className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-primary transition-colors shrink-0"
                          title="Edit customer code"
                          data-testid={`btn-edit-customer-code-${index}`}
                        >
                          <Pencil className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </td>
                  
                  <td className="p-1">
                    <div className="relative">
                      <SearchableSelect
                        options={products.map(p => ({ value: p.id, label: `${p.name}${p.sku ? ` (${p.sku})` : ''}` }))}
                        value={line.product_id}
                        onValueChange={(value) => onProductSelect(index, value)}
                        placeholder="Search product..."
                        searchPlaceholder="Type to search products..."
                        emptyMessage="No product found"
                        disabled={isReadOnly}
                        fallbackLabel={line.product_name}
                        data-testid={`select-product-${index}`}
                        className={line.supplier_code_locked && line.supplier_product_code ? 'border-amber-300 bg-amber-50/50' : ''}
                      />
                      {line.supplier_code_locked && line.supplier_product_code && (
                        <span className="absolute -top-1 -right-1 text-amber-500" title="Set by Customer Code - changing will clear it">
                          <Link2 className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </td>
                  
                  <td className="p-1 max-w-[110px]">
                    <div className="relative">
                      <Select
                        value={line.cobranding_id || ''}
                        onValueChange={(value) => onPackagingChange(index, value === 'none' ? '' : value)}
                        disabled={isReadOnly}
                      >
                        <SelectTrigger 
                          className={`h-9 ${line.supplier_code_locked && line.supplier_product_code ? 'border-amber-300 bg-amber-50/50' : ''}`} 
                          data-testid={`select-line-cobranding-${index}`}
                        >
                          <SelectValue placeholder="Standard" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Standard</SelectItem>
                          {cobrandings.map(cb => (
                            <SelectItem key={cb.id} value={cb.id}>{cb.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {line.supplier_code_locked && line.supplier_product_code && (
                        <span className="absolute -top-1 -right-1 text-amber-500" title="Set by Customer Code - changing will clear it">
                          <Link2 className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </td>
                  
                  <td className="p-1">
                    <Input
                      type="number" min="1"
                      value={line.quantity}
                      onChange={(e) => onUpdateLine(index, 'quantity', e.target.value)}
                      placeholder="0" disabled={isReadOnly}
                      className="h-9 text-right font-mono w-[80px]"
                      data-testid={`input-qty-${index}`}
                    />
                  </td>
                  
                  <td className="p-1">
                    <Input
                      type="number" min="0"
                      value={line.cases || ''}
                      onChange={(e) => onUpdateLine(index, 'cases', e.target.value)}
                      placeholder="0" disabled={isReadOnly}
                      className="h-9 text-right font-mono w-[70px]"
                      data-testid={`input-cases-${index}`}
                    />
                  </td>
                  
                  <td className="p-1">
                    <div className="relative flex items-center">
                      <Input
                        type="number" step="0.01" min="0"
                        value={line.estimated_weight}
                        onChange={(e) => onUpdateLine(index, 'estimated_weight', e.target.value)}
                        placeholder="0.00" disabled={isReadOnly}
                        className={`h-9 text-right font-mono w-[85px] ${
                          line.is_fixed_weight && parseFloat(line.weight_per_unit) > 0
                            ? 'bg-emerald-50 border-emerald-300 text-emerald-900'
                            : ''
                        }`}
                        data-testid={`input-weight-${index}`}
                        title={
                          line.is_fixed_weight && parseFloat(line.weight_per_unit) > 0
                            ? `Auto-weighed: each unit = ${parseFloat(line.weight_per_unit).toFixed(3)} kg. No manual weighing needed.`
                            : undefined
                        }
                      />
                      {line.is_fixed_weight && parseFloat(line.weight_per_unit) > 0 && (
                        <span
                          className="absolute -top-2 -right-1 inline-flex items-center gap-0.5 rounded-full bg-emerald-500 px-1.5 py-[1px] text-[8px] font-bold leading-none text-white shadow-sm"
                          data-testid={`badge-auto-weighed-${index}`}
                          aria-label="Auto-weighed fixed-weight product"
                        >
                          <span className="h-1 w-1 rounded-full bg-white"></span>
                          AUTO
                        </span>
                      )}
                    </div>
                  </td>
                  
                  <td className="p-1">
                    <Input
                      value={line.notes}
                      onChange={(e) => onUpdateLine(index, 'notes', e.target.value)}
                      placeholder="Notes" disabled={isReadOnly}
                      className="h-9 w-[90px]"
                      data-testid={`input-line-notes-${index}`}
                    />
                  </td>
                  
                  {!isReadOnly && (
                    <td className="p-1 text-center">
                      <Button
                        size="sm" variant="ghost"
                        onClick={() => onRemoveLine(index)}
                        disabled={lines.length === 1}
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                        data-testid={`btn-remove-line-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  )}
                </tr>
                {/* Formula status banner */}
                {!isReadOnly && line.product_id && (line.formula || line.formulaLearning) && (
                  <tr>
                    <td colSpan={isReadOnly ? 9 : 10} className="px-2 pb-1 pt-0">
                      {line.formula && !line.formulaOverride && (
                        <div className="flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded px-2.5 py-1" data-testid={`formula-banner-${index}`}>
                          <Lightbulb className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          <span className="text-emerald-700">
                            Auto-fill active: 1 unit = {line.formula.unit_weight_kg}kg | 1 case = {line.formula.units_per_case} units
                            {line.formula.source === 'learned' ? ' (learned)' : ''}
                          </span>
                          <label className="ml-auto flex items-center gap-1.5 cursor-pointer text-emerald-600 hover:text-emerald-800 select-none">
                            <Checkbox
                              checked={false}
                              onCheckedChange={() => onFormulaOverrideToggle && onFormulaOverrideToggle(index, true)}
                              className="h-3.5 w-3.5"
                              data-testid={`formula-override-${index}`}
                            />
                            <span>Override</span>
                          </label>
                        </div>
                      )}
                      {line.formula && line.formulaOverride && (
                        <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded px-2.5 py-1" data-testid={`formula-override-banner-${index}`}>
                          <ArrowRightLeft className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                          <span className="text-amber-700">Override active — auto-fill paused for this line</span>
                          <label className="ml-auto flex items-center gap-1.5 cursor-pointer text-amber-600 hover:text-amber-800 select-none">
                            <Checkbox
                              checked={true}
                              onCheckedChange={() => onFormulaOverrideToggle && onFormulaOverrideToggle(index, false)}
                              className="h-3.5 w-3.5"
                              data-testid={`formula-restore-${index}`}
                            />
                            <span>Override</span>
                          </label>
                        </div>
                      )}
                      {line.formulaLearning && !line.formula && (
                        <div className="flex items-center gap-2 text-xs bg-blue-50 border border-blue-200 rounded px-2.5 py-1" data-testid={`formula-learning-${index}`}>
                          <BookOpen className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                          <span className="text-blue-600">No formula yet — system will learn from this entry on save</span>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Order Total Box */}
        <div className="mt-4 flex justify-end">
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 min-w-[200px]">
            <h4 className="text-sm font-semibold mb-3">Order Total</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Units:</span>
                <span className="font-mono font-semibold">{totals.totalUnits}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Total Cases:</span>
                <span className="font-mono font-semibold">{totals.totalCases}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Est. Total Weight:</span>
                {weightInLbs ? (
                  <span className="font-mono font-semibold">
                    {totals.totalWeight.toFixed(2)} lbs
                    <span className="text-xs text-muted-foreground ml-1">
                      ({(totals.totalWeight * LBS_TO_KG).toFixed(2)} kg)
                    </span>
                  </span>
                ) : (
                  <span className="font-mono font-semibold">{totals.totalWeight.toFixed(2)} kg</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
